import * as i0 from '@angular/core';
import { Component, InjectionToken, Injectable, Inject, ViewChild, Input } from '@angular/core';
import { isNotNil, uuid as uuid$1 } from '@engineering11/utility';
import * as i2 from '@engineering11/web-api-error';
import { e11Error, toE11Error } from '@engineering11/web-api-error';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { QueryBuilder, updateOrAddCondition, conditions } from '@parse-it/database';
import { isEqual, cloneDeep, partition, isArray } from 'lodash';
import { combineLatest, map, distinctUntilChanged, debounceTime, filter, tap, switchMap as switchMap$1, mergeMap, Subject, take as take$1 } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import * as i1 from '@angular/router';
import { RestApiClient } from '@engineering11/web-api-rest';
import { E11ChartComponent } from '@engineering11/ui-lib/e11-chart';
import { LetDirective } from '@ngrx/component';
import { v4 } from 'uuid';
import * as i4 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i8 from '@engineering11/ui-lib/e11-button';
import { E11ButtonModule } from '@engineering11/ui-lib/e11-button';
import { E11DatePickerModule } from '@engineering11/ui-lib/e11-date-picker';
import * as i5 from '@engineering11/ui-lib/e11-divider';
import { E11DividerModule } from '@engineering11/ui-lib/e11-divider';
import * as i6 from '@engineering11/ui-lib/e11-form-builder';
import { E11DynamicFormModule } from '@engineering11/ui-lib/e11-form-builder';
import { E11InputCheckboxModule } from '@engineering11/ui-lib/e11-input-checkbox';
import { E11MultiSelectModule } from '@engineering11/ui-lib/e11-multi-select';
import { E11SelectModule } from '@engineering11/ui-lib/e11-select';
import * as i7 from '@engineering11/ui-lib/e11-ui-utils';

const SDK_NAME = 'reporting';
const COLLECTIONS = Object.freeze({});
var ReportingPermissions;
(function (ReportingPermissions) {
    ReportingPermissions["QueryReporting"] = "reporting/query-reporting";
})(ReportingPermissions || (ReportingPermissions = {}));

class ReportingComponent {
    constructor() {
        this.title = 'reporting';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ReportingComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: ReportingComponent, isStandalone: true, selector: "reporting-root", providers: [], ngImport: i0, template: ``, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ReportingComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'reporting-root',
                    standalone: true,
                    template: ``,
                    imports: [],
                    providers: [],
                }]
        }] });

const REPORTING_CONFIG_TOKEN = new InjectionToken('reporting-config-provider');
class BaseReportingConfigProvider {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: BaseReportingConfigProvider, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: BaseReportingConfigProvider, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: BaseReportingConfigProvider, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

const initialState$1 = {
    globalFilter: [],
    groupingFilterMap: {},
};
class ChartPageStore extends ComponentStore {
    constructor(router, activatedRoute, errorHandler) {
        super(initialState$1);
        this.router = router;
        this.activatedRoute = activatedRoute;
        this.errorHandler = errorHandler;
        // Selectors remain unchanged
        this.getState = this.select((s) => s);
        this.globalFilters$ = this.select(({ globalFilter }) => globalFilter);
        this.groupingFilterMap$ = this.select(({ groupingFilterMap }) => groupingFilterMap);
        this.filtersByGroupingIds$ = (groupingIds) => this.select(({ groupingFilterMap }) => Object.entries(groupingFilterMap)
            .filter(([key, value]) => groupingIds.includes(key) && value)
            .map(([key, value]) => value)
            .filter(isNotNil)
            .flat());
        // ----- Effects & Updaters -----
        this.syncFilterToQueryParam = this.effect((trigger$) => combineLatest([trigger$, this.groupingFilterMap$]).pipe(map(([_, groupingFilterMap]) => groupingFilterMap), distinctUntilChanged(), debounceTime(500), switchMap((groupingFilterMap) => {
            const queryParams = {
                groupingFilterMap: JSON.stringify(groupingFilterMap),
            };
            this.router.navigate([], {
                relativeTo: this.activatedRoute,
                queryParams,
                queryParamsHandling: 'merge',
            });
            return [];
        })));
        this.onAddFilter = this.effect((filterReq$) => filterReq$.pipe(map(({ filter, groupingIds }) => {
            if (groupingIds) {
                this.addGroupFilter({ filter, groupingIds });
            }
            else {
                this.addGlobalFilter(filter);
            }
        })));
        this.onResetFilters = this.effect((_$) => _$.pipe(map((_) => {
            return this.resetCharts();
        })));
        this.addGlobalFilter = this.updater((s, globalFilter) => ({
            ...s,
            globalFilter,
        }));
        this.addGroupFilter = this.updater((s, filterReq) => ({
            ...s,
            groupingFilterMap: this.updateGroupFilters(s.groupingFilterMap, filterReq.filter, filterReq.groupingIds),
        }));
        this.resetCharts = this.updater(() => ({
            globalFilter: [],
            groupingFilterMap: {},
        }));
        this.activatedRoute.queryParams.pipe(take(1)).subscribe((params) => {
            this.setUpInitialValue(params);
        });
        this.syncFilterToQueryParam();
    }
    setUpInitialValue({ groupingFilterMap }) {
        if (groupingFilterMap) {
            try {
                const parsedGroupingFilterMap = JSON.parse(groupingFilterMap);
                this.patchState({ groupingFilterMap: parsedGroupingFilterMap });
            }
            catch (error) {
                this.errorHandler.handleError(new Error('Error parsing groupingFilterMap from queryParams'));
            }
        }
    }
    updateGroupFilters(record, filter, groupingIds) {
        if (!groupingIds)
            throw e11Error({ title: 'No Grouping Ids Provided', type: 'reporting/no-grouping-ids' });
        const newRecord = updateRecordKeys(record, groupingIds, undefined);
        groupingIds.forEach((id) => (newRecord[id] = filter));
        return newRecord;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ChartPageStore, deps: [{ token: i1.Router }, { token: i1.ActivatedRoute }, { token: i2.E11ErrorHandlerService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ChartPageStore, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ChartPageStore, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: i1.Router }, { type: i1.ActivatedRoute }, { type: i2.E11ErrorHandlerService }] });
// Utility function to update record keys
function updateRecordKeys(record, newKeys, defaultValue) {
    newKeys.forEach((key) => {
        if (!(key in record))
            record[key] = defaultValue;
    });
    return record;
}

class ReportingRestService {
    constructor(configProvider) {
        this.configProvider = configProvider;
        this.restClient = new RestApiClient({
            baseUrl: this.configProvider.config.reportingBaseURL,
            token: this.configProvider.config.token$.pipe(filter(isNotNil)),
            throwNon200s: true,
        });
    }
    query(query) {
        return this.restClient.post('reporting', query);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ReportingRestService, deps: [{ token: REPORTING_CONFIG_TOKEN }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ReportingRestService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ReportingRestService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: BaseReportingConfigProvider, decorators: [{
                    type: Inject,
                    args: [REPORTING_CONFIG_TOKEN]
                }] }] });

const initialState = {
    chartId: undefined,
    queryNode: undefined,
    groupingIds: [],
    data: undefined,
    loading: false,
    activeRequestId: undefined,
    offset: undefined,
    limit: undefined,
    loadingMore: false,
    hasMoreData: true,
    filterTransformationFn: undefined,
};
/**
 * Listens to filters from ChartPageStore and dispatches requests
 */
class ChartStore extends ComponentStore {
    constructor(chartPageStore, errorHandler, reportingRestService) {
        super(initialState);
        this.chartPageStore = chartPageStore;
        this.errorHandler = errorHandler;
        this.reportingRestService = reportingRestService;
        this.queryBuilder = new QueryBuilder();
        // Selectors
        this.getState = this.select((s) => s);
        this.queryNode$ = this.select(({ queryNode }) => queryNode);
        this.groupingIds$ = this.select(({ groupingIds }) => groupingIds);
        // Will need to be able to generically establish the type of these
        this.data$ = this.select(({ data }) => data);
        this.loading$ = this.select(({ loading }) => loading);
        this.loadingMore$ = this.select(({ loadingMore }) => loadingMore);
        this.hasMoreData$ = this.select(({ hasMoreData }) => hasMoreData);
        this.pagination$ = this.select((s) => ({ offset: s.offset, limit: s.limit }), { debounce: true });
        this.loadMore = this.effect((_$) => _$.pipe(tap(() => {
            const state = this.get();
            if (!state.data || !state.limit || !state.hasMoreData)
                return;
            this.setLoadingMore(true);
            this.setPagination({
                offset: (state.offset ?? 0) + state.limit,
            });
        })));
        this.updateFilters = this.effect((trigger$) => combineLatest([trigger$, this.groupingIds$]).pipe(switchMap$1(([_, groupingIds]) => combineLatest([
            this.chartPageStore.globalFilters$.pipe(distinctUntilChanged(isEqual), tap(() => {
                const state = this.get();
                if (state.offset !== undefined && state.limit !== undefined) {
                    this.resetPagination();
                }
            })),
            this.chartPageStore.filtersByGroupingIds$(groupingIds).pipe(distinctUntilChanged(isEqual), tap(() => {
                const state = this.get();
                if (state.offset !== undefined && state.limit !== undefined) {
                    this.resetPagination();
                }
            })),
            this.queryNode$.pipe(filter(isNotNil)),
            this.pagination$.pipe(distinctUntilChanged((prev, curr) => prev.offset === curr.offset && prev.limit === curr.limit)),
        ]).pipe(tap(() => (this.get().loadingMore ? null : this.setLoading(true))), mergeMap(([globalFilters, groupFilters, queryNode, _pagination]) => {
            const { query, requestId } = this.constructQuery(globalFilters, groupFilters, queryNode);
            return this.reportingRestService.query(query).pipe(tapResponse((res) => {
                this.setUpdatedData({
                    data: res.data,
                    requestId: requestId,
                    append: !!this.get().offset,
                });
            }, (e) => this.errorHandler.handleError(e), () => {
                this.setLoading(false);
                this.setLoadingMore(false);
            }));
        })))));
        this.setFilterTransformationFn = this.updater((s, filterTransformationFn) => ({
            ...s,
            filterTransformationFn,
        }));
        this.setGroupingIds = this.updater((s, groupingIds) => ({
            ...s,
            groupingIds: groupingIds ?? [],
        }));
        this.setQuery = this.updater((s, query) => {
            const { limit, offset, ...rest } = query;
            return {
                ...s,
                queryNode: rest,
                limit,
                offset,
            };
        });
        this.setUpdatedData = this.updater((s, data) => {
            if (s.activeRequestId !== data.requestId)
                return s;
            const newData = data.append && s.data ? [...s.data, ...data.data] : data.data;
            return {
                ...s,
                data: newData,
                hasMoreData: s.limit ? data.data.length >= s.limit : true,
            };
        });
        this.setActiveRequestId = this.updater((s, id) => ({
            ...s,
            activeRequestId: id,
        }));
        this.setLoading = this.updater((s, loading) => ({
            ...s,
            loading,
        }));
        this.setPagination = this.updater((s, pagination) => ({
            ...s,
            offset: pagination.offset ?? s.offset,
            limit: pagination.limit ?? s.limit,
        }));
        this.resetPagination = this.updater((s) => ({
            ...s,
            offset: s.offset !== undefined && s.limit !== undefined ? 0 : undefined,
            limit: s.offset !== undefined && s.limit !== undefined ? s.limit : undefined,
            hasMoreData: true,
        }));
        this.setLoadingMore = this.updater((s, loadingMore) => ({
            ...s,
            loadingMore,
        }));
        this.updateFilters();
    }
    constructQuery(globalFilters, groupFilters, queryNode) {
        const defaultFilter = queryNode?.where ?? { type: 'filter', operator: 'AND', conditions: [] };
        const query = cloneDeep(queryNode);
        const filtersTransformationFn = this.get().filterTransformationFn;
        if (filtersTransformationFn) {
            query.where = filtersTransformationFn(globalFilters, groupFilters, query);
        }
        else {
            query.where = groupFilters.concat(globalFilters).reduce((prev, curr) => updateOrAddCondition(prev, curr), defaultFilter);
        }
        const state = this.get();
        if (state.limit !== undefined) {
            query.offset = state.offset;
            query.limit = state.limit;
        }
        this.validateJSON(query);
        const requestId = uuid$1();
        this.setActiveRequestId(requestId);
        return { query, requestId };
    }
    validateJSON(queryNode) {
        try {
            this.queryBuilder.build(queryNode);
        }
        catch (err) {
            const invalidQueryError = toE11Error(err, {
                title: 'Invalid Query Node',
                type: 'reporting/invalid-query',
                additionalData: queryNode,
            });
            this.errorHandler.error('Invalid Query Node', queryNode);
            throw invalidQueryError;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ChartStore, deps: [{ token: ChartPageStore }, { token: i2.E11ErrorHandlerService }, { token: ReportingRestService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ChartStore }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: ChartStore, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: ChartPageStore }, { type: i2.E11ErrorHandlerService }, { type: ReportingRestService }] });

function uuid() {
    return v4();
}
function htmlId(prefix = 'id') {
    return `${prefix}_${uuid()}`;
}
function dateBetween(column, values) {
    column = column ?? 'timestamp';
    if (values.length === 1) {
        return {
            type: 'expression',
            left: { type: 'expression', left: `TIMESTAMP_TRUNC(${column}, DAY)` },
            operator: '=',
            right: { type: 'expression', left: `TIMESTAMP_TRUNC('${values[0]}', DAY)` },
        };
    }
    return betweenDateCondition(column, values[0], values[1]);
}
function betweenDateCondition(column, startDate, endDate) {
    return {
        type: 'expression',
        left: {
            type: 'expression',
            left: column,
        },
        operator: 'BETWEEN',
        right: {
            type: 'expression',
            left: `'${startDate}' AND '${endDate}'`,
        },
    };
}
const mapper = (value, filterConfig) => {
    const defaultFilter = { type: 'filter', operator: 'AND', conditions: [] };
    const [hasTransform, noTransform] = partition(Object.entries(value), ([key, value]) => {
        const k = key;
        return filterConfig[k] && 'transform' in filterConfig[k];
    });
    const hasTransformExpressions = hasTransform.reduce((prev, curr) => {
        const k = curr[0];
        const res = filterConfig[k]?.transform(curr[1]) ?? [];
        return prev.concat(isArray(res) ? res : [res]);
    }, []);
    const expressionList = noTransform
        .filter(([key, value]) => value !== null)
        .map(([key, val]) => {
        return conditions([{ column: key, operator: '=', value: val }]);
    })
        .concat(hasTransformExpressions);
    return expressionList.reduce((prev, curr) => {
        return updateOrAddCondition(prev, curr);
    }, defaultFilter).conditions;
};
/**
 * Supports creating conditions for list of values that may contain a null value
 * Converts values into conditions of IN/NOT IN (non null values) OR IS/IS NOT NULL
 *
 * @param values - list of values of type string or number, which may include null string
 * @param column - column to query in BQ
 * @param nullString - String used as a placeholder to represent null
 * @param listOperation - IN or NOT IN (used for querying list)
 * @param nullOperation - IS or IS NOT (used for querying against null)
 * @returns - expression node representing desired query
 */
function listConditionNullTransform(values, column, nullString, listOperation = 'IN', nullOperation = 'IS') {
    const conditionList = [];
    if (!values || values.length === 0)
        return [];
    const index = values.indexOf(nullString);
    if (index !== -1) {
        conditionList.push({ column: column, operator: nullOperation, value: null });
        values.splice(index, 1);
    }
    if (values.length === 0)
        return [conditions(conditionList)];
    const filteredValues = values.filter((val) => val !== nullString);
    conditionList.push({ column: column, operator: listOperation, value: filteredValues });
    return [conditions(conditionList, 'OR')];
}

class SDKChart {
    set groupingIds(groupingIds) {
        if (!groupingIds || groupingIds.length === 0)
            return;
        this._groupingIds = groupingIds;
        this.chartStore.setGroupingIds(this._groupingIds);
    }
    set queryNode(inputStuff) {
        const { queryNode, filterTransformationFn } = inputStuff;
        this._queryNode = queryNode;
        this.chartStore.setFilterTransformationFn(filterTransformationFn);
        this.chartStore.setQuery(queryNode);
    }
    constructor(chartStore, chartPageStore) {
        this.chartStore = chartStore;
        this.chartPageStore = chartPageStore;
        this._groupingIds = [uuid$1()];
        this.chartId = htmlId('sdk-chart');
        this.type = 'line';
        this.seriesData$ = this.chartStore.data$.pipe(distinctUntilChanged(), map((data) => (this.dataTransformationFn && data ? this.dataTransformationFn(data) : data)));
        this.xAxisData$ = this.chartStore.data$.pipe(distinctUntilChanged(), map((data) => (this.xAxisDataFn && data ? this.xAxisDataFn(data) : undefined)));
        this.loading$ = this.chartStore.loading$;
        this.destroy$ = new Subject();
    }
    ngOnInit() {
        this.chartStore.setGroupingIds(this._groupingIds);
    }
    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.complete();
    }
    async chartClicked(event) {
        if (!this.clickTransformationFn)
            return;
        const createdFilter = this.clickTransformationFn(event);
        this.chartPageStore.onAddFilter({
            filter: [createdFilter],
            groupingIds: this._groupingIds,
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SDKChart, deps: [{ token: ChartStore }, { token: ChartPageStore }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: SDKChart, isStandalone: true, selector: "sdk-chart", inputs: { chartId: "chartId", type: "type", title: "title", groupingIds: "groupingIds", chartOpts: "chartOpts", queryNode: "queryNode", clickTransformationFn: "clickTransformationFn", dataTransformationFn: "dataTransformationFn", xAxisDataFn: "xAxisDataFn", globalFormFilterMapperFn: "globalFormFilterMapperFn" }, providers: [ChartStore], viewQueries: [{ propertyName: "chart", first: true, predicate: E11ChartComponent, descendants: true }], ngImport: i0, template: `
    <ng-container *ngrxLet="seriesData$ as seriesData">
      <ng-container *ngrxLet="xAxisData$ as xAxisData">
        <ng-container *ngrxLet="loading$ as loading">
          <e11-chart
            [chartId]="chartId"
            [type]="type"
            [series]="seriesData ?? [{ data: [] }]"
            [loading]="loading"
            [width]="chartOpts?.width || '100%'"
            [height]="chartOpts?.height || 400"
            [title]="title"
            [subtitle]="chartOpts?.subtitle"
            [showLegend]="chartOpts?.showLegend || true"
            [yAxisName]="chartOpts?.yAxisName"
            [xAxisName]="chartOpts?.xAxisName"
            [legendPosition]="chartOpts?.legendPosition || 'bottom'"
            [legendOrient]="chartOpts?.legendOrient || 'horizontal'"
            [legendDistance]="chartOpts?.legendDistance || '0%'"
            [showLabelOnMobile]="chartOpts?.showLabelOnMobile || false"
            [xAxisData]="xAxisData ?? chartOpts?.xAxisData"
            [lineGradient]="
              chartOpts?.lineGradient || {
                startColor: 'rgb(255, 158, 68)',
                endColor: 'rgb(255, 70, 131'
              }
            "
            [inlineBarHeight]="chartOpts?.inlineBarHeight || 50"
            (chartClick)="chartClicked($event)"
          ></e11-chart>
        </ng-container>
      </ng-container>
    </ng-container>
  `, isInline: true, dependencies: [{ kind: "component", type: E11ChartComponent, selector: "e11-chart", inputs: ["chartId", "type", "series", "width", "height", "title", "subtitle", "showLegend", "yAxisName", "xAxisName", "legendPosition", "legendOrient", "legendDistance", "showLabelOnMobile", "xAxisData", "zoomEnd", "showDataZoom", "lineGradient", "inlineBarHeight", "loading"], outputs: ["chartClick", "chartDblClick", "chartMouseDown", "chartMouseMove", "chartMouseUp", "chartMouseOver", "chartMouseOut", "chartGlobalOut", "chartContextMenu", "chartOutsideClick"] }, { kind: "directive", type: LetDirective, selector: "[ngrxLet]", inputs: ["ngrxLet", "ngrxLetSuspenseTpl"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SDKChart, decorators: [{
            type: Component,
            args: [{
                    selector: 'sdk-chart',
                    standalone: true,
                    imports: [E11ChartComponent, LetDirective],
                    providers: [ChartStore],
                    template: `
    <ng-container *ngrxLet="seriesData$ as seriesData">
      <ng-container *ngrxLet="xAxisData$ as xAxisData">
        <ng-container *ngrxLet="loading$ as loading">
          <e11-chart
            [chartId]="chartId"
            [type]="type"
            [series]="seriesData ?? [{ data: [] }]"
            [loading]="loading"
            [width]="chartOpts?.width || '100%'"
            [height]="chartOpts?.height || 400"
            [title]="title"
            [subtitle]="chartOpts?.subtitle"
            [showLegend]="chartOpts?.showLegend || true"
            [yAxisName]="chartOpts?.yAxisName"
            [xAxisName]="chartOpts?.xAxisName"
            [legendPosition]="chartOpts?.legendPosition || 'bottom'"
            [legendOrient]="chartOpts?.legendOrient || 'horizontal'"
            [legendDistance]="chartOpts?.legendDistance || '0%'"
            [showLabelOnMobile]="chartOpts?.showLabelOnMobile || false"
            [xAxisData]="xAxisData ?? chartOpts?.xAxisData"
            [lineGradient]="
              chartOpts?.lineGradient || {
                startColor: 'rgb(255, 158, 68)',
                endColor: 'rgb(255, 70, 131'
              }
            "
            [inlineBarHeight]="chartOpts?.inlineBarHeight || 50"
            (chartClick)="chartClicked($event)"
          ></e11-chart>
        </ng-container>
      </ng-container>
    </ng-container>
  `,
                }]
        }], ctorParameters: () => [{ type: ChartStore }, { type: ChartPageStore }], propDecorators: { chart: [{
                type: ViewChild,
                args: [E11ChartComponent]
            }], chartId: [{
                type: Input
            }], type: [{
                type: Input
            }], title: [{
                type: Input
            }], groupingIds: [{
                type: Input
            }], chartOpts: [{
                type: Input
            }], queryNode: [{
                type: Input,
                args: [{ required: true }]
            }], clickTransformationFn: [{
                type: Input
            }], dataTransformationFn: [{
                type: Input
            }], xAxisDataFn: [{
                type: Input
            }], globalFormFilterMapperFn: [{
                type: Input
            }] } });

class SDKReportingPage {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SDKReportingPage, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: SDKReportingPage, isStandalone: true, selector: "sdk-reporting-page", ngImport: i0, template: ` <ng-content></ng-content> `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SDKReportingPage, decorators: [{
            type: Component,
            args: [{
                    selector: 'sdk-reporting-page',
                    standalone: true,
                    template: ` <ng-content></ng-content> `,
                }]
        }] });

class SDKReportFilteringComponent {
    set form(value) {
        this.fieldGroups = value;
    }
    constructor(chartPageStore, router, activatedRoute, errorHandler, location) {
        this.chartPageStore = chartPageStore;
        this.router = router;
        this.activatedRoute = activatedRoute;
        this.errorHandler = errorHandler;
        this.location = location;
        this.loading = false;
        this.containerClass = '';
        this.label = '';
        this.addToStore = false;
        this.activatedRoute.queryParams.pipe(take$1(1)).subscribe((params) => {
            this.setUpInitialValue(params);
        });
    }
    setUpInitialValue({ globalFormFilter }) {
        if (globalFormFilter && !this.initialValue) {
            try {
                const parsedGlobalFilter = JSON.parse(globalFormFilter);
                this.initialValue = parsedGlobalFilter;
            }
            catch (error) {
                this.errorHandler.handleError(new Error('Error parsing globalFormFilter from queryParams'));
            }
        }
    }
    valueChanges(model) {
        if (!this.initialValue)
            this.initialValue = model;
        this.updateQueryParams(model);
        const filterValues = this.filterConfig.valueChange ? this.filterConfig.valueChange.transform(model) : mapper(model, this.filterConfig);
        this.chartPageStore.onAddFilter({ filter: isArray(filterValues) ? filterValues : [filterValues] });
    }
    updateQueryParams(model) {
        const queryParams = {
            globalFormFilter: JSON.stringify(model),
        };
        const url = this.router
            .createUrlTree([], {
            relativeTo: this.activatedRoute,
            queryParams,
            queryParamsHandling: 'merge',
        })
            .toString();
        this.location.replaceState(url);
    }
    resetFilters() {
        this.filterFormBuilder.form.reset(this.initialValue);
        this.chartPageStore.onResetFilters();
        this.updateQueryParams(this.initialValue);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SDKReportFilteringComponent, deps: [{ token: ChartPageStore }, { token: i1.Router }, { token: i1.ActivatedRoute }, { token: i2.E11ErrorHandlerService }, { token: i4.Location }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: SDKReportFilteringComponent, isStandalone: true, selector: "sdk-report-filtering", inputs: { form: "form", loading: "loading", initialValue: "initialValue", filterConfig: "filterConfig", containerClass: "containerClass", label: "label" }, viewQueries: [{ propertyName: "filterFormBuilder", first: true, predicate: ["filterFormBuilder"], descendants: true }], ngImport: i0, template: `
    <div class=" {{ containerClass }}">
    <e11-button class="e11-flex e11-justify-end" [ghost]="true" [size]="'sm'" (click)="resetFilters()" value="Reset Filters"></e11-button>

      <e11-dynamic-form-builder
        #filterFormBuilder
        [fieldGroups]="fieldGroups"
        [hideSubmitMenu]="true"
        [initialValue]="initialValue"
        [loading]="loading"
        (valueChanges)="valueChanges($event)"
      >
      <ng-template e11Template="dateRangeLabel">
      <h5 class="e11-font-semibold e11-my-2">{{label}}</h5>

      </ng-template>
      <ng-template e11Template="divider">
        <e11-divider [margin]="1"/>
      </ng-template>
      </e11-dynamic-form-builder>
      <e11-button class="e11-flex e11-justify-end" [ghost]="true" [size]="'sm'" (click)="resetFilters()" value="Reset Filters"></e11-button>
    </div>
  `, isInline: true, dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "ngmodule", type: E11DatePickerModule }, { kind: "ngmodule", type: E11DividerModule }, { kind: "component", type: i5.E11DividerComponent, selector: "e11-divider", inputs: ["text", "style", "textStyle", "textBg", "color", "height", "width", "bgcolor", "panel", "margin", "iconSize", "icon", "iconColor", "hasCircle"] }, { kind: "ngmodule", type: E11SelectModule }, { kind: "ngmodule", type: E11InputCheckboxModule }, { kind: "ngmodule", type: E11MultiSelectModule }, { kind: "ngmodule", type: E11DynamicFormModule }, { kind: "component", type: i6.E11DynamicFormBuilderComponent, selector: "e11-dynamic-form-builder", inputs: ["initialValue", "hideSubmitMenu", "loading", "disabled", "disabledText", "submitButtonConfig", "cancelButtonConfig", "deleteButtonConfig", "cancelLinkConfig", "showCancelLink", "showCancelButton", "showDeleteButton", "submitButtonTitle", "cancelLinkTitle", "cancelButtonTitle", "deleteButtonTitle", "buttonColorSubmit", "buttonColorCancel", "buttonColorDelete", "fieldGroups", "errorMessageOverrides"], outputs: ["valueChanges", "onSubmit", "onCancel", "onDelete"] }, { kind: "directive", type: i7.E11Template, selector: "[e11Template]", inputs: ["type", "e11Template"] }, { kind: "ngmodule", type: E11ButtonModule }, { kind: "component", type: i8.E11ButtonComponent, selector: "e11-button", inputs: ["color", "ghost", "fontColor", "id", "name", "type", "value", "size", "style", "buttonStyle", "loading", "skeletonLoading", "loadingText", "display", "buttonGroup", "paddingX", "icon", "iconType", "symbol", "symbolType", "iconSize", "iconPosition", "iconClassOverrides", "containerClassOverrides", "disabledText", "disabled", "titleText", "titleTextPosition", "autofocus"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SDKReportFilteringComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sdk-report-filtering',
                    standalone: true,
                    imports: [
                        CommonModule,
                        E11DatePickerModule,
                        E11DividerModule,
                        E11SelectModule,
                        E11InputCheckboxModule,
                        E11MultiSelectModule,
                        E11DynamicFormModule,
                        E11ButtonModule,
                    ],
                    template: `
    <div class=" {{ containerClass }}">
    <e11-button class="e11-flex e11-justify-end" [ghost]="true" [size]="'sm'" (click)="resetFilters()" value="Reset Filters"></e11-button>

      <e11-dynamic-form-builder
        #filterFormBuilder
        [fieldGroups]="fieldGroups"
        [hideSubmitMenu]="true"
        [initialValue]="initialValue"
        [loading]="loading"
        (valueChanges)="valueChanges($event)"
      >
      <ng-template e11Template="dateRangeLabel">
      <h5 class="e11-font-semibold e11-my-2">{{label}}</h5>

      </ng-template>
      <ng-template e11Template="divider">
        <e11-divider [margin]="1"/>
      </ng-template>
      </e11-dynamic-form-builder>
      <e11-button class="e11-flex e11-justify-end" [ghost]="true" [size]="'sm'" (click)="resetFilters()" value="Reset Filters"></e11-button>
    </div>
  `,
                }]
        }], ctorParameters: () => [{ type: ChartPageStore }, { type: i1.Router }, { type: i1.ActivatedRoute }, { type: i2.E11ErrorHandlerService }, { type: i4.Location }], propDecorators: { filterFormBuilder: [{
                type: ViewChild,
                args: ['filterFormBuilder']
            }], form: [{
                type: Input
            }], loading: [{
                type: Input
            }], initialValue: [{
                type: Input
            }], filterConfig: [{
                type: Input
            }], containerClass: [{
                type: Input
            }], label: [{
                type: Input
            }] } });

/*
 * Public API Surface of reporting
 */

/**
 * Generated bundle index. Do not edit.
 */

export { BaseReportingConfigProvider, COLLECTIONS, ChartPageStore, ChartStore, REPORTING_CONFIG_TOKEN, ReportingComponent, ReportingPermissions, ReportingRestService, SDKChart, SDKReportFilteringComponent, SDKReportingPage, SDK_NAME, dateBetween, htmlId, listConditionNullTransform, mapper, uuid };
